/**
 * WorkspacePresenceStore — Real-time presence data for sidebar glow + avatars.
 *
 * sl-jmyl.6: Direct DI on WorkspaceRealtimeService (same WorkspaceScope).
 *   1. Initial fetch via GraphQL query (reliable, on mount)
 *   2. Push updates via WorkspaceRealtimeService.workspacePresence$ (Direct Socket)
 *
 * RBAC: Filters against AccessibleFolderCacheStore (only accessible docs shown).
 * Workflow documents are in no doc list: they come apart, by client document
 * ID, and show only beside a workflow the workflow list already holds.
 *
 * SOLID:
 * - S: Single Responsibility — Only presence data aggregation + RBAC filtering
 * - D: Dependency Inversion — Depends on Store/LiveData abstractions
 */
import type { WorkspaceServerService } from '@affine/core/modules/cloud/services/workspace-server';
import type { AccessibleDocCacheStore } from '@affine/core/modules/doc/stores/accessible-doc-cache';
import type {
  PresenceDocEntry,
  WorkspaceRealtimeService,
} from '@affine/core/modules/editor/services/workspace-realtime.service';
import { getWorkspacePresenceQuery } from '@affine/graphql';
import { isLocalWorkflowDocId } from '@streamlain/workflow-field-schemas';
import { LiveData, Store } from '@toeverything/infra';
import { Subscription } from 'rxjs';

import type { WorkspaceShareSettingService } from '../../share-setting';
import type {
  AccessibleFolderCacheStore,
  AccessibleFolderMeta,
} from './accessible-folder-cache';

/** An assistant of a running chat turn and the doc it works on. */
export interface WorkingAssistant {
  sessionId: string;
  ownerId: string;
  name: string | null;
  activity: string | null;
  docId: string;
}

/** Assistants only show at docs the viewer may read. */
export function computeWorkingAssistants(
  docs: PresenceDocEntry[],
  accessibleDocIds: Set<string>
): WorkingAssistant[] {
  return docs.flatMap(doc =>
    accessibleDocIds.has(doc.docId)
      ? (doc.agents ?? []).map(agent => ({ ...agent, docId: doc.docId }))
      : []
  );
}

export interface PresenceUserInfo {
  id: string;
  name?: string | null;
  avatarUrl?: string | null;
}

export function computeWorkspacePresence(
  docs: Array<{ docId: string; userIds: string[] }>,
  accessibleDocIds: Set<string>,
  folders: AccessibleFolderMeta[]
): {
  docPresence: Map<string, string[]>;
  glowingNodeIds: Set<string>;
} {
  const foldersById = new Map(folders.map(folder => [folder.id, folder]));
  const contentFolders = folders.filter(folder => !folder.metadataOnly);

  const docPresence = new Map<string, string[]>();
  const glowingNodeIds = new Set<string>();
  const visibleFolderIds = new Set(contentFolders.map(folder => folder.id));

  for (const doc of docs) {
    // RBAC filter: only show presence for docs the user can access
    if (!accessibleDocIds.has(doc.docId)) continue;
    if (doc.userIds.length === 0) continue;

    // Dedupe: the realtime/presence stream reports every open session
    // (tab + desktop app each count), so a user with multiple sessions
    // appears multiple times in userIds.
    const uniqueUserIds = Array.from(new Set(doc.userIds)).sort();
    docPresence.set(doc.docId, uniqueUserIds);
    glowingNodeIds.add(doc.docId);

    for (const folder of contentFolders) {
      if (!folder.docIds.includes(doc.docId)) continue;

      let currentId: string | null = folder.id;
      while (currentId) {
        if (!visibleFolderIds.has(currentId)) {
          break;
        }
        glowingNodeIds.add(currentId);
        const current = foldersById.get(currentId);
        currentId = current?.parentId ?? null;
      }
    }
  }

  return { docPresence, glowingNodeIds };
}

function stringArraysEqual(a: string[] | undefined, b: string[] | undefined) {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

function stringMapsEqual(a: Map<string, string[]>, b: Map<string, string[]>) {
  if (a.size !== b.size) return false;
  for (const [key, value] of a) {
    if (!stringArraysEqual(value, b.get(key))) return false;
  }
  return true;
}

function stringSetsEqual(a: Set<string>, b: Set<string>) {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

export class WorkspacePresenceStore extends Store {
  constructor(
    private readonly accessibleFolderCache: AccessibleFolderCacheStore,
    private readonly accessibleDocCache: AccessibleDocCacheStore,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly realtimeService: WorkspaceRealtimeService,
    private readonly settings: WorkspaceShareSettingService
  ) {
    super();
  }

  readonly enabled$ = LiveData.computed(
    get => get(this.settings.sharePreview.presenceDiscoveryEnabled$) === true
  );

  /** Map of docId → userIds currently viewing that doc */
  readonly docPresence$ = new LiveData<Map<string, string[]>>(new Map());
  readonly docPresenceNames$ = new LiveData<
    Map<string, ReadonlyMap<string, string>>
  >(new Map());

  /**
   * Map of workflow document ID (as the client names it) → userIds in it.
   * Names are in docPresenceNames$ under the same ID.
   */
  readonly workflowDocPresence$ = new LiveData<Map<string, string[]>>(
    new Map()
  );

  /** Assistants at work in docs the viewer may read */
  readonly assistants$ = new LiveData<WorkingAssistant[]>([]);

  /** Set of all node IDs (docIds + folderIds) that should glow */
  readonly glowingNodeIds$ = new LiveData<Set<string>>(new Set());

  private subscriptions: Subscription | null = null;
  private listeningWorkspaceId: string | null = null;
  private releaseRealtimePresence: (() => void) | null = null;
  private presenceDocs: PresenceDocEntry[] = [];
  private presenceUpdateVersion = 0;
  private accessibleDocIds = new Set<string>();
  private accessibleFolders: AccessibleFolderMeta[] = [];

  /**
   * Start listening to presence events and fetch initial state.
   * sl-jmyl.6: Subscribes to WorkspaceRealtimeService.workspacePresence$ (Direct Socket).
   * sl-jmyl.11: Eagerly connects the /realtime socket so presence updates
   *   arrive even before the user opens a doc.
   */
  startListening(workspaceId: string): void {
    if (this.listeningWorkspaceId === workspaceId && this.subscriptions) {
      return;
    }

    this.stopListening();
    this.listeningWorkspaceId = workspaceId;
    this.subscriptions = new Subscription();

    this.subscriptions.add(
      this.enabled$.subscribe(() => this.recomputePresence())
    );

    // Subscribe to push updates via Direct Socket (WorkspaceRealtimeService)
    this.subscriptions.add(
      this.realtimeService.workspacePresence$.subscribe(docs => {
        this.presenceUpdateVersion += 1;
        this.handlePresenceUpdate(docs);
      })
    );

    this.subscriptions.add(
      this.accessibleDocCache.watchAllAccessibleDocs().subscribe(docs => {
        this.accessibleDocIds = new Set(docs.map(doc => doc.id));
        this.recomputePresence();
      })
    );

    this.subscriptions.add(
      this.accessibleFolderCache
        .watchAllAccessibleFolders()
        .subscribe(folders => {
          this.accessibleFolders = folders;
          this.recomputePresence();
        })
    );

    this.disposables.push(() => {
      this.stopListening();
    });

    // Eagerly keep /realtime alive for push updates (no doc needed).
    this.releaseRealtimePresence =
      this.realtimeService.retainWorkspacePresence();

    // Fetch initial state via GraphQL (one-time, reliable)
    void this.fetchInitialPresence(workspaceId);
  }

  private async fetchInitialPresence(workspaceId: string): Promise<void> {
    const server = this.workspaceServerService.server;
    if (!server) return;
    const presenceUpdateVersion = this.presenceUpdateVersion;

    try {
      const result = await server.gql({
        query: getWorkspacePresenceQuery,
        variables: { workspaceId },
      });
      const docs = result.workspace.presence;
      if (
        this.listeningWorkspaceId !== workspaceId ||
        this.presenceUpdateVersion !== presenceUpdateVersion
      ) {
        return;
      }
      this.handlePresenceUpdate(docs);
    } catch {
      // Initial fetch failed — push updates will still work
    }
  }

  private handlePresenceUpdate(docs: PresenceDocEntry[]): void {
    this.presenceDocs = docs;
    this.recomputePresence();
  }

  private recomputePresence(): void {
    const { docPresence, glowingNodeIds } = computeWorkspacePresence(
      this.enabled$.value ? this.presenceDocs : [],
      this.accessibleDocIds,
      this.accessibleFolders
    );

    const workflowDocPresence = new Map<string, string[]>();
    for (const doc of this.enabled$.value ? this.presenceDocs : []) {
      if (!isLocalWorkflowDocId(doc.docId) || doc.userIds.length === 0) {
        continue;
      }
      workflowDocPresence.set(
        doc.docId,
        Array.from(new Set(doc.userIds)).sort()
      );
    }

    const names = new Map<string, ReadonlyMap<string, string>>();
    for (const doc of this.presenceDocs) {
      if (
        (!docPresence.has(doc.docId) && !workflowDocPresence.has(doc.docId)) ||
        !doc.users
      ) {
        continue;
      }
      names.set(
        doc.docId,
        new Map(
          doc.users
            .filter(user => doc.userIds.includes(user.id))
            .map(user => [user.id, user.name])
        )
      );
    }
    this.docPresenceNames$.setValue(names);
    const assistants = computeWorkingAssistants(
      this.enabled$.value ? this.presenceDocs : [],
      this.accessibleDocIds
    );
    if (JSON.stringify(assistants) !== JSON.stringify(this.assistants$.value)) {
      this.assistants$.setValue(assistants);
    }
    if (!stringMapsEqual(this.docPresence$.value, docPresence)) {
      this.docPresence$.setValue(docPresence);
    }
    if (
      !stringMapsEqual(this.workflowDocPresence$.value, workflowDocPresence)
    ) {
      this.workflowDocPresence$.setValue(workflowDocPresence);
    }
    if (!stringSetsEqual(this.glowingNodeIds$.value, glowingNodeIds)) {
      this.glowingNodeIds$.setValue(glowingNodeIds);
    }
  }

  private stopListening(): void {
    this.presenceUpdateVersion += 1;
    this.subscriptions?.unsubscribe();
    this.subscriptions = null;
    this.releaseRealtimePresence?.();
    this.releaseRealtimePresence = null;
    this.listeningWorkspaceId = null;
    this.presenceDocs = [];
    this.accessibleDocIds = new Set();
    this.accessibleFolders = [];
    this.docPresence$.setValue(new Map());
    this.docPresenceNames$.setValue(new Map());
    this.assistants$.setValue([]);
    this.glowingNodeIds$.setValue(new Set());
  }

  override dispose(): void {
    this.stopListening();
    super.dispose();
  }
}
